import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { InboxArrowDownIcon } from '@heroicons/react/24/outline';

import { Button, IconButton, Logo } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';
import { ReferralRequest } from '@restworld/data-services';

import { ReferralRequestsModal } from './referral-requests-modal';

interface ReferralHeaderProps {
  requestCount: number;
  referralRequests: ReferralRequest[];
}

/**
 * Top navigation bar for the referral page.
 * Contains a back button, centered Restworld logo, and "Your requests" button with notification badge.
 */
export function ReferralHeader({ requestCount, referralRequests }: ReferralHeaderProps) {
  const { t } = useTranslation('referral-page');
  const router = useRouter();
  const [isRequestsModalOpen, setIsRequestsModalOpen] = useState(false);

  return (
    <>
      <HeaderContainer>
        <LeftSection>
          <IconButton Icon={ArrowLeftIcon} ariaLabel="Go back" onClick={() => router.back()} />
        </LeftSection>

        <CenterSection>
          <Logo variant="horizontal" title="Restworld" className="h-8 w-auto text-purple-700" />
        </CenterSection>

        <RightSection>
          <RequestButtonWrapper>
            <MobileOnly>
              <IconButton
                Icon={InboxArrowDownIcon}
                ariaLabel={t('your_requests')}
                onClick={() => setIsRequestsModalOpen(true)}
              />
            </MobileOnly>
            <DesktopOnly>
              <Button variant="tertiary" StartIcon={InboxArrowDownIcon} onClick={() => setIsRequestsModalOpen(true)}>
                {t('your_requests')}
              </Button>
            </DesktopOnly>
            {requestCount > 0 && <NotificationBadge>{requestCount}</NotificationBadge>}
          </RequestButtonWrapper>
        </RightSection>
      </HeaderContainer>

      <ReferralRequestsModal
        isOpen={isRequestsModalOpen}
        onClose={() => setIsRequestsModalOpen(false)}
        requests={referralRequests}
      />
    </>
  );
}

export default ReferralHeader;

// Layout Components
const HeaderContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center justify-between w-full px-4 py-3 sm:py-0', className)} {...props} />
);

const LeftSection = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center gap-2.5 flex-1', className)} {...props} />
);

const CenterSection = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center justify-center', className)} {...props} />
);

const RightSection = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center justify-end flex-1', className)} {...props} />
);

const MobileOnly = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('block sm:hidden', className)} {...props} />
);

const DesktopOnly = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('hidden sm:block', className)} {...props} />
);

const RequestButtonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('relative', className)} {...props} />
);

const NotificationBadge = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span
    className={cn(
      'absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-xs font-semibold',
      className
    )}
    {...props}
  />
);
